// Who a reply goes to — the one place that decides it, for both Reply and Reply all.
//
// 🔴 [2026-10-01] Reply used to copy the original Cc straight into Cc, which made it a *partial*
// reply-all: everyone on Cc got the answer, while everyone on To was silently dropped. One button
// doing most of two jobs is worse than either, so the rule lives here and both buttons call it.
//
// 🚩 Measured on this box's own 7,627 cached messages before writing: `to_email` arrives in THREE
// shapes — 6,053 JSON arrays, 7 comma-separated lists, 1,567 bare single addresses — and none of
// them carry a display name. The old code only tried `JSON.parse` with a raw-string fallback, so a
// comma-list was taken as ONE address and the reply went to a string no MTA would accept.
//
// 🔒 Pure on purpose: no fetch, no DOM, no clock. The hazard here is set arithmetic on addresses,
// which is exactly what a unit test can pin down and a browser test cannot.

/** Split one stored recipient field into bare addresses, whichever of the three shapes it is in. */
export function parseAddrs(raw: string | null | undefined): string[] {
  const s = String(raw ?? '').trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    // A JSON array is the shape sync writes today.
    try {
      const a = JSON.parse(s);
      if (Array.isArray(a)) return a.map(one).filter(Boolean) as string[];
    } catch { /* malformed — fall through and treat it as text */ }
  }
  return s.split(',').map(one).filter(Boolean) as string[];
}

// A display name would come as `Name <a@b.com>`; none of the stored rows have one, but a reply must
// not break the day sync starts keeping them.
function one(v: unknown): string {
  // 🚩 The text fallback is reached by MALFORMED JSON as well as by a plain comma-list, so a token can
  // still be wearing `[`, `]` or quotes. Caught by the test: `["a@x.com"` (truncated array) came back
  // as the address `["a@x.com"`, which is worse than dropping it — it would have been handed to SMTP.
  let t = String(v ?? '').trim().replace(/^[[\s"']+|[\]\s"',]+$/g, '').trim();
  if (!t) return '';
  const m = /<([^>]+)>/.exec(t);
  return (m ? m[1] : t).trim();
}

const key = (a: string) => a.trim().toLowerCase();

export interface ReplyTargetMessage {
  fromEmail?: string | null;
  toEmail?: string | null;
  ccEmail?: string | null;
}

/**
 * `all: false` — just the sender. `all: true` — the sender plus everyone who was on To, with the
 * original Cc kept as Cc. Your own address is removed from both either way, and nobody appears twice.
 */
export function replyRecipients(
  msg: ReplyTargetMessage,
  myEmail: string | string[] | null | undefined,
  all: boolean,
): { to: string[]; cc: string[] } {
  // All of my mailboxes' addresses: From can be any of them (B-08), so none may be answered.
  const mine = new Set((Array.isArray(myEmail) ? myEmail : [myEmail]).map((a) => key(String(a ?? ''))).filter(Boolean));
  const from = parseAddrs(msg.fromEmail);
  const origTo = parseAddrs(msg.toEmail);
  const origCc = parseAddrs(msg.ccEmail);

  // 🚩 Replying to something YOU sent (from the Sent folder, or your own message in a thread): the
  // sender is you, so answering "the sender" would address an empty field. The people to answer are
  // the ones it went to. Without this, reply-all on your own mail reaches nobody.
  const fromIsMe = from.length > 0 && from.every((a) => mine.has(key(a)));
  const base = fromIsMe ? origTo : from;

  const seen = new Set<string>();
  for (const k of mine) seen.add(k);
  const take = (list: string[]) => {
    const out: string[] = [];
    for (const a of list) {
      const k = key(a);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(a);
    }
    return out;
  };

  // Order matters: To is built first so an address on both To and Cc lands in To and is not repeated.
  const to = take(all && !fromIsMe ? [...base, ...origTo] : base);
  const cc = all ? take(origCc) : [];
  return { to, cc };
}
